import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDropzone } from 'react-dropzone';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../ui/alert-dialog';
import {
  Image, Plus, Trash2, X, Upload, Loader2, Download,
  ChevronLeft, ChevronRight, Calendar, ZoomIn,
  CheckCircle, AlertCircle, Star
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UploadStatus = {
  PENDING: 'pending',
  UPLOADING: 'uploading',
  SUCCESS: 'success',
  ERROR: 'error'
};

function FileUploadItem({ file, status, progress, onRemove }) {
  return (
    <div className="flex items-center gap-3 p-2 bg-slate-50 dark:bg-slate-800 rounded-lg">
      <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-slate-200 dark:bg-slate-700">
        {file.preview ? (
          <img src={file.preview} alt={file.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Image className="w-5 h-5 text-slate-400" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{file.name}</p>
        <p className="text-xs text-slate-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
        {status === UploadStatus.UPLOADING && (
          <Progress value={progress} className="h-1 mt-1" />
        )}
      </div>
      <div className="flex-shrink-0">
        {status === UploadStatus.PENDING && (
          <Button variant="ghost" size="sm" onClick={onRemove} className="h-8 w-8 p-0">
            <X className="w-4 h-4" />
          </Button>
        )}
        {status === UploadStatus.UPLOADING && (
          <Loader2 className="w-5 h-5 text-orange-500 animate-spin" />
        )}
        {status === UploadStatus.SUCCESS && (
          <CheckCircle className="w-5 h-5 text-green-500" />
        )}
        {status === UploadStatus.ERROR && (
          <AlertCircle className="w-5 h-5 text-red-500" />
        )}
      </div>
    </div>
  );
}

export function R2PhotoGallery({ tripId, itineraryItems = [], isOrganizer = false, hideHeader = false, onOpenUpload, photoCountRef }) {
  const { token } = useAuth();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [selectedItinerary, setSelectedItinerary] = useState('');
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Fetch photos
  const fetchPhotos = useCallback(async () => {
    if (!tripId || !token) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/storage/trips/${tripId}/files?folder=fotos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPhotos(data);
      }
    } catch (error) {
      console.error('Error fetching photos:', error);
    } finally {
      setLoading(false);
    }
  }, [tripId, token]);

  useEffect(() => {
    fetchPhotos();
  }, [fetchPhotos]);

  // Expor função para abrir upload dialog (para uso externo via callback)
  useEffect(() => {
    if (onOpenUpload) {
      onOpenUpload(() => setUploadDialogOpen(true));
    }
  }, [onOpenUpload]);

  // Expor contagem de fotos via ref
  useEffect(() => {
    if (photoCountRef) {
      photoCountRef.current = photos.length;
    }
  }, [photos.length, photoCountRef]);

  // Dropzone
  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    rejectedFiles.forEach(({ file, errors }) => {
      errors.forEach(error => {
        if (error.code === 'file-too-large') {
          toast.error(`${file.name}: Arquivo muito grande (máx. 50MB)`);
        } else if (error.code === 'file-invalid-type') {
          toast.error(`${file.name}: Apenas imagens são permitidas`);
        }
      });
    });

    const newFiles = acceptedFiles.map(file => ({
      file,
      id: `${file.name}-${Date.now()}-${Math.random()}`,
      name: file.name,
      size: file.size,
      type: file.type,
      preview: URL.createObjectURL(file),
      status: UploadStatus.PENDING,
      progress: 0
    }));

    setFiles(prev => [...prev, ...newFiles].slice(0, 20));
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/gif': ['.gif'],
      'image/webp': ['.webp']
    },
    maxSize: 50 * 1024 * 1024,
    maxFiles: 20 - files.length,
    disabled: uploading || files.length >= 20
  });

  const removeFile = (id) => {
    setFiles(prev => {
      const file = prev.find(f => f.id === id);
      if (file?.preview) URL.revokeObjectURL(file.preview);
      return prev.filter(f => f.id !== id);
    });
  };

  const uploadFiles = async () => {
    if (files.length === 0 || uploading) return;

    setUploading(true);
    const pendingFiles = files.filter(f => f.status === UploadStatus.PENDING);
    let successCount = 0;

    for (const fileObj of pendingFiles) {
      try {
        setFiles(prev => prev.map(f => 
          f.id === fileObj.id ? { ...f, status: UploadStatus.UPLOADING, progress: 10 } : f
        ));

        // Get presigned URL
        const urlRes = await fetch(`${API_URL}/api/storage/trips/${tripId}/upload-url`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            filename: fileObj.name,
            content_type: fileObj.type,
            folder: 'fotos',
            itinerary_item_id: selectedItinerary || null
          })
        });

        if (!urlRes.ok) {
          const errData = await urlRes.json().catch(() => ({}));
          throw new Error(errData.detail || 'Erro ao obter URL de upload');
        }
        
        const { upload_url, file_id } = await urlRes.json();

        setFiles(prev => prev.map(f => 
          f.id === fileObj.id ? { ...f, progress: 30 } : f
        ));

        // Upload to R2
        const uploadRes = await fetch(upload_url, {
          method: 'PUT',
          headers: { 'Content-Type': fileObj.type },
          body: fileObj.file
        });

        if (!uploadRes.ok) {
          throw new Error('Erro no upload para o servidor');
        }

        setFiles(prev => prev.map(f => 
          f.id === fileObj.id ? { ...f, progress: 70 } : f
        ));

        // Confirm upload
        const confirmRes = await fetch(`${API_URL}/api/storage/trips/${tripId}/confirm-upload`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            file_id: file_id,
            file_size_bytes: fileObj.size
          })
        });

        if (!confirmRes.ok) {
          throw new Error('Erro ao confirmar upload');
        }

        setFiles(prev => prev.map(f => 
          f.id === fileObj.id ? { ...f, status: UploadStatus.SUCCESS, progress: 100 } : f
        ));
        successCount++;

      } catch (error) {
        console.error('Upload error:', error);
        toast.error(error.message || 'Erro no upload');
        setFiles(prev => prev.map(f => 
          f.id === fileObj.id ? { ...f, status: UploadStatus.ERROR, progress: 0 } : f
        ));
      }
    }

    setUploading(false);

    if (successCount > 0) {
      toast.success(`${successCount} foto(s) enviada(s)!`);
      fetchPhotos();
      
      await new Promise(resolve => setTimeout(resolve, 1500));
      setFiles(prev => prev.filter(f => f.status !== UploadStatus.SUCCESS));
      if (files.filter(f => f.status === UploadStatus.ERROR).length === 0) {
        setUploadDialogOpen(false);
        setSelectedItinerary('');
      }
    }
  };

  // Delete photo
  const handleDelete = async (photoId) => {
    try {
      const res = await fetch(`${API_URL}/api/storage/trips/${tripId}/files/${photoId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Foto excluída');
        fetchPhotos();
        if (lightboxOpen) setLightboxOpen(false);
      }
    } catch (error) {
      toast.error('Erro ao excluir foto');
    }
  };

  // Set as cover
  const handleSetCover = async (photoId) => {
    try {
      const res = await fetch(`${API_URL}/api/storage/trips/${tripId}/files/${photoId}/set-cover`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Foto definida como capa!');
        fetchPhotos();
      }
    } catch (error) {
      toast.error('Erro ao definir capa');
    }
  };

  // Download photo
  const handleDownload = async (photo) => {
    try {
      const res = await fetch(`${API_URL}/api/storage/trips/${tripId}/files/${photo.id}/download-url`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error();
      
      const { download_url } = await res.json();
      
      const link = document.createElement('a');
      link.href = download_url;
      link.download = photo.original_filename;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      toast.error('Erro ao baixar foto');
    }
  };

  const openLightbox = (index) => {
    setCurrentPhotoIndex(index);
    setLightboxOpen(true);
  };

  const nextPhoto = () => setCurrentPhotoIndex((prev) => (prev + 1) % photos.length);
  const prevPhoto = () => setCurrentPhotoIndex((prev) => (prev - 1 + photos.length) % photos.length);

  const pendingCount = files.filter(f => f.status === UploadStatus.PENDING).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Upload Dialog - Movido para fora do header */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="sm:max-w-lg !p-0">
          <DialogHeader className="p-4 sm:p-6 pb-2">
            <DialogTitle>Enviar Fotos</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 px-4 sm:px-6 pb-4">
            {/* Itinerary selection */}
            {itineraryItems.length > 0 && (
              <div>
                <Label htmlFor="itinerary">Vincular a atividade (opcional)</Label>
                <select
                  id="itinerary"
                  value={selectedItinerary}
                  onChange={(e) => setSelectedItinerary(e.target.value)}
                  className="w-full mt-1 p-2 border rounded-md bg-white dark:bg-slate-800 dark:border-slate-700"
                >
                  <option value="">Galeria geral</option>
                  {itineraryItems.map((item) => (
                    <option key={item.id} value={item.id}>{item.title}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Dropzone */}
            <div
              {...getRootProps()}
              className={`
                border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors
                ${isDragActive 
                  ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20' 
                  : 'border-slate-300 dark:border-slate-600 hover:border-orange-400'
                }
                ${uploading || files.length >= 20 ? 'opacity-50 cursor-not-allowed' : ''}
              `}
            >
              <input {...getInputProps()} data-testid="photo-file-input" />
              <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />
              <p className="text-sm text-slate-600 dark:text-slate-300">
                {isDragActive ? 'Solte as fotos aqui...' : 'Arraste fotos ou clique para selecionar'}
              </p>
                <p className="text-xs text-slate-400 mt-1">
                  JPG, PNG, GIF, WebP até 50MB
                </p>
              </div>

              {/* File list */}
              {files.length > 0 && (
                <div className="max-h-48 overflow-y-auto space-y-2">
                  {files.map(file => (
                    <FileUploadItem
                      key={file.id}
                      file={file}
                      status={file.status}
                      progress={file.progress}
                      onRemove={() => removeFile(file.id)}
                    />
                  ))}
                </div>
              )}

              {/* Upload button */}
              {pendingCount > 0 && (
                <Button
                  onClick={uploadFiles}
                  disabled={uploading}
                  className="w-full bg-orange-500 hover:bg-orange-600"
                  data-testid="confirm-upload-btn"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Enviar {pendingCount} foto(s)
                    </>
                  )}
                </Button>
              )}
            </div>
        </DialogContent>
      </Dialog>

      {/* Header condicional - só mostra se não receber hideHeader */}
      {!hideHeader && (
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
              Galeria de Fotos
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {photos.length} foto{photos.length !== 1 ? 's' : ''}
            </p>
          </div>
          <Button 
            className="bg-orange-500 hover:bg-orange-600" 
            data-testid="upload-photo-btn"
            onClick={() => setUploadDialogOpen(true)}
          >
            <Plus className="w-4 h-4 mr-2" />
            Enviar Fotos
          </Button>
        </div>
      )}

      {/* Contador de fotos quando header está oculto */}
      {hideHeader && photos.length > 0 && (
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {photos.length} foto{photos.length !== 1 ? 's' : ''}
        </p>
      )}

      {/* Gallery Grid */}
      {photos.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Image className="w-12 h-12 text-slate-300 dark:text-slate-600 mb-4" />
            <p className="text-slate-500 dark:text-slate-400 text-center">
              Nenhuma foto na galeria ainda
            </p>
            <p className="text-sm text-slate-400 dark:text-slate-500 text-center mt-1">
              Compartilhe momentos da viagem com o grupo
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <AnimatePresence>
            {photos.map((photo, index) => (
              <PhotoThumbnail
                key={photo.id}
                photo={photo}
                index={index}
                tripId={tripId}
                token={token}
                onClick={() => openLightbox(index)}
                onDownload={() => handleDownload(photo)}
              />
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxOpen && photos[currentPhotoIndex] && (
          <PhotoLightbox
            photo={photos[currentPhotoIndex]}
            tripId={tripId}
            token={token}
            currentIndex={currentPhotoIndex}
            totalPhotos={photos.length}
            onClose={() => setLightboxOpen(false)}
            onPrev={prevPhoto}
            onNext={nextPhoto}
            onDelete={handleDelete}
            onSetCover={handleSetCover}
            onDownload={handleDownload}
            isOrganizer={isOrganizer}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// Photo thumbnail with download button
function PhotoThumbnail({ photo, index, tripId, token, onClick, onDownload }) {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadImage = async () => {
      try {
        const res = await fetch(`${API_URL}/api/storage/trips/${tripId}/files/${photo.id}/download-url`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setImageUrl(data.download_url);
        }
      } catch (error) {
        console.error('Error loading thumbnail:', error);
      } finally {
        setLoading(false);
      }
    };
    loadImage();
  }, [photo.id, tripId, token]);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ delay: index * 0.05 }}
      className="group relative aspect-square rounded-xl overflow-hidden bg-slate-100 dark:bg-slate-800"
    >
      {loading ? (
        <div className="w-full h-full flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : imageUrl ? (
        <img
          src={imageUrl}
          alt={photo.original_filename}
          className="w-full h-full object-cover cursor-pointer transition-transform group-hover:scale-105"
          loading="lazy"
          onClick={onClick}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <Image className="w-8 h-8 text-slate-400" />
        </div>
      )}
      
      {/* Overlay with actions */}
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors pointer-events-none" />
      
      {/* Zoom icon */}
      <div 
        className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
        onClick={onClick}
      >
        <ZoomIn className="w-8 h-8 text-white" />
      </div>
      
      {/* Download button */}
      <button
        onClick={(e) => { e.stopPropagation(); onDownload(); }}
        className="absolute bottom-2 right-2 p-2 rounded-full bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
        title="Baixar foto"
      >
        <Download className="w-4 h-4" />
      </button>
      
      {/* Cover badge */}
      {photo.is_cover && (
        <div className="absolute top-2 left-2">
          <Badge className="bg-orange-500 text-white text-xs">
            <Star className="w-3 h-3 mr-1 fill-current" />
            Capa
          </Badge>
        </div>
      )}
      
      {/* Activity badge */}
      {photo.itinerary_item_title && (
        <div className="absolute bottom-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <Badge variant="secondary" className="text-xs">
            <Calendar className="w-3 h-3 mr-1" />
            {photo.itinerary_item_title}
          </Badge>
        </div>
      )}
    </motion.div>
  );
}

// Lightbox component
function PhotoLightbox({ 
  photo, tripId, token, currentIndex, totalPhotos, 
  onClose, onPrev, onNext, onDelete, onSetCover, onDownload, isOrganizer 
}) {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadImage = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/storage/trips/${tripId}/files/${photo.id}/download-url`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setImageUrl(data.download_url);
        }
      } catch (error) {
        console.error('Error loading image:', error);
      } finally {
        setLoading(false);
      }
    };
    loadImage();
  }, [photo.id, tripId, token]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
      onClick={onClose}
    >
      {/* Close button */}
      <button
        className="absolute top-4 right-4 p-2 text-white/70 hover:text-white transition-colors z-10"
        onClick={onClose}
      >
        <X className="w-8 h-8" />
      </button>

      {/* Navigation */}
      {totalPhotos > 1 && (
        <>
          <button
            className="absolute left-4 p-2 text-white/70 hover:text-white transition-colors z-10"
            onClick={(e) => { e.stopPropagation(); onPrev(); }}
          >
            <ChevronLeft className="w-10 h-10" />
          </button>
          <button
            className="absolute right-4 p-2 text-white/70 hover:text-white transition-colors z-10"
            onClick={(e) => { e.stopPropagation(); onNext(); }}
          >
            <ChevronRight className="w-10 h-10" />
          </button>
        </>
      )}

      {/* Image */}
      {loading ? (
        <Loader2 className="w-12 h-12 animate-spin text-white" />
      ) : imageUrl ? (
        <motion.img
          key={photo.id}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          src={imageUrl}
          alt={photo.original_filename}
          className="max-h-[85vh] max-w-[90vw] object-contain"
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <div className="text-white">Erro ao carregar imagem</div>
      )}

      {/* Info bar */}
      <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
        <div className="max-w-3xl mx-auto flex items-center justify-between text-white">
          <div>
            <div className="flex items-center gap-2">
              <p className="text-lg">{photo.original_filename}</p>
              {photo.is_cover && (
                <Badge className="bg-orange-500 text-white text-xs">
                  <Star className="w-3 h-3 mr-1 fill-current" />
                  Capa
                </Badge>
              )}
            </div>
            <p className="text-sm text-white/70">
              por {photo.uploaded_by_name} • {photo.file_size_formatted}
              {photo.created_at && ` • ${format(new Date(photo.created_at), "dd/MM/yyyy", { locale: ptBR })}`}
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Download */}
            <Button 
              variant="ghost" 
              size="sm"
              onClick={(e) => { e.stopPropagation(); onDownload(photo); }}
              className="text-white/70 hover:text-white"
            >
              <Download className="w-4 h-4 mr-1" />
              Baixar
            </Button>
            
            {/* Set as cover */}
            {isOrganizer && !photo.is_cover && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={(e) => { e.stopPropagation(); onSetCover(photo.id); }}
                className="text-white/70 hover:text-orange-400"
              >
                <Star className="w-4 h-4 mr-1" />
                Definir Capa
              </Button>
            )}
            
            {/* Delete */}
            {isOrganizer && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" size="icon" className="text-white/70 hover:text-red-400">
                    <Trash2 className="w-5 h-5" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Excluir foto?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Esta ação não pode ser desfeita.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => onDelete(photo.id)}
                      className="bg-red-500 hover:bg-red-600"
                    >
                      Excluir
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>
      </div>

      {/* Counter */}
      <div className="absolute top-4 left-4 text-white/70 text-sm">
        {currentIndex + 1} / {totalPhotos}
      </div>
    </motion.div>
  );
}

export default R2PhotoGallery;
